import {
  IconTelegram,
  IconWhatsapp,
  IconFacebook,
  IconVK,
  IconTwitterShare,
  IconCopyShare,
} from "@lib/icons"
import {
  TelegramShareButton,
  WhatsappShareButton,
  FacebookShareButton,
  VKShareButton,
  TwitterShareButton,
} from "react-share"
import classnames from "classnames"
import { useEffect, useState } from "react"
import { isServer } from "@lib/isomorphic"
import { observer } from "mobx-react"
import { useStore } from "@features/common"
import { PostElementBackground } from "@serializers/article"
import { ArticleShareType } from "models/analytics.model"

interface ArticleShareProps {
  background: PostElementBackground
}

export const ArticleShare = observer(function ArticleShare(
  props: ArticleShareProps,
) {
  const [url, setUrl] = useState("")
  const server = isServer()
  const [isTooltipOpened, setTooltipOpened] = useState(false)
  const {
    BlogModel: { postDetails, getTemplate },
    AnalyticsModel: { articleShareClickHandler },
  } = useStore()
  const iconFillColor = props.background === "black" ? "white" : "black"

  const tooltipClassnames = classnames(
    "fixed top-24 px-6 py-3 text-white font-bold bg-black z-70 rounded-md text-[18px] w-[250px] left-[calc(50%-125px)] text-center",
    {
      "opacity-0": !isTooltipOpened,
      "opacity-100": isTooltipOpened,
      "transition-opacity pointer-events-none": true,
    },
  )
  const sectionClassnames = classnames(
    "pt-6 pb-16 w-full tablet:pt-8 tablet:pb-18 desktop:pb-20 mt-[-1px]",
  )

  const handleCopy = () => {
    navigator.clipboard.writeText(url)
    articleShareClickHandler("copy_link")
    setTooltipOpened(true)
    setTimeout(() => setTooltipOpened(false), 5000)
  }

  const handleAnalyticsEvent = (type: ArticleShareType) => {
    articleShareClickHandler(type)
  }

  useEffect(() => {
    if (!isServer()) {
      setUrl(window.location.href)
    }
  }, [server, postDetails?.id])

  return (
    <section className={sectionClassnames}>
      <div className={tooltipClassnames}>{getTemplate("copied")}</div>
      <div className="container space-y-6 w-full desktop:space-y-8">
        <div className="flex items-center tablet:flex-row flex-col">
          <div className="tracking-[1.5px] text-[18px] leading-6 mb-6 tablet:mb-0 text-left w-full tablet:w-auto">
            {getTemplate("article.share")}
          </div>
          <div className="flex justify-between tablet:justify-start w-full tablet:w-auto">
            <TwitterShareButton
              url={url}
              className="tablet:ml-6"
              onClick={() => handleAnalyticsEvent("twitter")}
              data-test-id="article__share_twitter"
            >
              <IconTwitterShare fill={iconFillColor} />
            </TwitterShareButton>
            <WhatsappShareButton
              url={url}
              className="ml-6"
              onClick={() => handleAnalyticsEvent("whatsapp")}
              data-test-id="article__share_whatsapp"
            >
              <IconWhatsapp fill={iconFillColor} />
            </WhatsappShareButton>
            <TelegramShareButton
              url={url}
              className="ml-6"
              onClick={() => handleAnalyticsEvent("telegram")}
              data-test-id="article__share_telegram"
            >
              <IconTelegram fill={iconFillColor} />
            </TelegramShareButton>
            <FacebookShareButton
              url={url}
              className="ml-6"
              onClick={() => handleAnalyticsEvent("facebook")}
              data-test-id="article__share_facebook"
            >
              <IconFacebook fill={iconFillColor} />
            </FacebookShareButton>
            <VKShareButton
              url={url}
              className="ml-6"
              onClick={() => handleAnalyticsEvent("vk")}
              data-test-id="article__share_vk"
            >
              <IconVK fill={iconFillColor} />
            </VKShareButton>
            <a
              className="cursor-pointer ml-6"
              onClick={handleCopy}
              data-test-id="article__share_copy"
            >
              <IconCopyShare fill={iconFillColor} />
            </a>
          </div>
        </div>
      </div>
    </section>
  )
})
